import { ListButton } from "@refinedev/antd";
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  Flex,
  Spin,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Button,
  theme,
} from "antd";
import {
  ArrowLeftOutlined,
  CopyOutlined,
  EditOutlined,
  LeftOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useGetIdentity } from "@refinedev/core";
import { axiosInstance } from "@/utilities";
import { SellerIcon } from "@/components";
import { useOperatorStores } from "@/hooks/useOperatorStores";
import { API_URL_PIM_PRODUCTS } from "@/services/XCONS_ENV";
import type {
  CatalogDetail,
  OfferScope,
  ProductParticipants,
  ProductStoresResponse,
  StoreOfferRow,
  StoreVisibilityRow,
} from "./types";
import { HistorialDelProducto } from "./HistorialDelProducto";
import { PermisosDelProducto } from "./PermisosDelProducto";
import { DuplicarProducto } from "./DuplicarProducto";
import { ProductStateBar, useEstadoDelProducto } from "./ProductStateBar";
import { ProductStateSteps } from "./ProductStateSteps";
import { CabeceraFija } from "./CabeceraFija";
import {
  Adjuntos,
  Bloque,
  Campo,
  CategoriasPorArbol,
  ImagenesYVideos,
  Relacionados,
  SeccionesDeLaFicha,
  Vacio,
  cantidadDeVinculos,
  seccionesDelForm,
  valorPublicado,
} from "./FichaSoloLectura";
import { etiquetaConjunto } from "./conjuntos";
import { skuFabricanteDe } from "./sku-xcons";
import { storesConCategorias } from "./stores-con-categorias";

const { Text, Title } = Typography;

/**
 * Detalle **read-only** del producto.
 *
 * **Tiene la estructura del formulario** (xcons-docs#401), con `FichaSoloLectura`: las
 * secciones y su orden salen del conjunto del producto, así que un atributo nuevo aparece
 * solo, y en el mismo lugar que en el form.
 *
 * **No pide el esquema completo del conjunto**, que pesa 295 KB por las 8.162 opciones de
 * sus select. El detalle ya trae los grupos sin opciones más `selectedLabels` —la etiqueta
 * de lo que el producto tiene elegido—, y con eso alcanza para mostrarlo. Las opciones sólo
 * las necesita el formulario de edición, que las pide una vez por conjunto y las cachea.
 */
export const CatalogDetailPage: React.FC = () => {
  const { sku = "" } = useParams<{ sku: string }>();
  const navigate = useNavigate();

  const [producto, setProducto] = useState<CatalogDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; code?: string } | null>(
    null,
  );
  // Una transición puede cambiar el `status` nativo, así que el detalle se vuelve a
  // pedir: si no, la ficha sigue mostrando los valores de antes de publicar.
  const [recarga, setRecarga] = useState(0);
  const [acceso, setAcceso] = useState<ProductParticipants | null>(null);
  const [duplicando, setDuplicando] = useState(false);
  /** Una sola lectura del estado para el flujo de la cabecera y la barra de validación. */
  // Quién lo vende y la visibilidad por tienda salen del mismo pedido: se hace una vez, acá.
  const oferta = useOfertaDelProducto(producto?.magentoProductId);
  // Las categorías se ven por árbol, sólo los de los stores donde tiene Categorías.
  const { data: identity } = useGetIdentity<any>();
  const estado = useEstadoDelProducto(producto?.magentoProductId, () =>
    setRecarga((n) => n + 1),
  );

  useEffect(() => {
    let cancelado = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axiosInstance.get<CatalogDetail>(
          `${API_URL_PIM_PRODUCTS}/${encodeURIComponent(sku)}`,
        );
        if (!cancelado) setProducto(data);
      } catch (e) {
        const res = (
          e as { response?: { status?: number; data?: { message?: string } } }
        )?.response;
        if (!cancelado) {
          setError({
            message: res?.data?.message ?? "No se pudo cargar el producto",
            // El 409 es el SKU duplicado en Magento, y merece un mensaje propio: no es
            // un error del operador ni algo que pueda resolver desde acá.
            code: res?.status === 409 ? "duplicado" : undefined,
          });
        }
      } finally {
        if (!cancelado) setLoading(false);
      }
    };

    run();
    return () => {
      cancelado = true;
    };
  }, [sku, recarga]);

  /**
   * El nivel propio sobre este producto, que es lo que decide si el detalle es
   * editable. Se pide aparte del producto porque el read model del catálogo es común a
   * todos y esto es por operador; y se pide **siempre**, no sólo al abrir el panel,
   * porque el aviso de read-only tiene que estar antes de que el operador intente algo.
   */
  useEffect(() => {
    if (!producto?.magentoProductId) return;
    let cancelado = false;

    axiosInstance
      .get<ProductParticipants>(
        `${API_URL_PIM_PRODUCTS}/${producto.magentoProductId}/participants`,
      )
      .then(({ data }) => {
        if (!cancelado) setAcceso(data);
      })
      .catch(() => undefined);

    return () => {
      cancelado = true;
    };
  }, [producto?.magentoProductId]);

  /**
   * El SKU del fabricante que el SKU lleva adentro (`FISCHER-608511` → `608511`). No es un
   * atributo: se deriva con la misma regla que usa el form. `null` en los SKUs viejos que
   * no siguen `MARCA-SKU Fabricante`, y ahí no se muestra.
   */
  const marca = producto?.selectedLabels?.manufacturer;
  const skuFabricante = producto
    ? skuFabricanteDe(producto.sku, Array.isArray(marca) ? marca[0] : marca)
    : null;

  const media = producto?.media ?? [];
  const adjuntos = producto?.attachments ?? [];

  // Al entrar desde la tabla, un spinner y no un esqueleto (xcons-docs#404).
  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message={error.message}
          description={
            error.code === "duplicado"
              ? "Hay más de un producto con este SKU en Magento. Hay que resolver el duplicado antes de poder abrirlo por SKU."
              : undefined
          }
        />
        <Button
          style={{ marginTop: 16 }}
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/catalog")}
        >
          Volver al catálogo
        </Button>
      </Card>
    );
  }

  if (!producto) return null;

  /**
   * El dueño y el nivel propio pueden haber cambiado en Permisos: se re-piden para que el
   * aviso de read-only y la opción Editar no queden viejos.
   */
  const refrescarAcceso = () => {
    axiosInstance
      .get<ProductParticipants>(
        `${API_URL_PIM_PRODUCTS}/${producto.magentoProductId}/participants`,
      )
      .then(({ data }) => setAcceso(data))
      .catch(() => undefined);
  };

  /**
   * **Duplicar y Editar van junto al título, sólo el ícono** (xcons-docs#432), no en el
   * menú del flujo: ninguno de los dos cambia el estado. Sin nivel se muestran
   * deshabilitados y el tooltip dice por qué.
   *
   * Editar sobre un producto que se está vendiendo abre la **versión de edición**: lo
   * publicado se sigue vendiendo hasta que se apruebe. Uno pausado se edita directo (R16).
   */
  const puedeEditar = !!acceso?.ownLevel;
  /**
   * Duplicar crea un producto nuevo, así que lo habilita lo mismo que muestra "Nuevo
   * producto" en el listado: cualquier nivel del Catálogo (xcons-docs#594). Quien duplica
   * queda autor del borrador, y dueño al publicarlo.
   */
  const puedeCrear =
    !!estado.data?.catalogLevel && estado.data.catalogLevel !== "no_access";

  /**
   * **Con una versión de edición en curso, ni Editar ni Duplicar** (xcons-docs#479): editar
   * abriría otra sobre la misma, y duplicar copiaría lo publicado sin lo que se está
   * cambiando. Se ven en gris y dicen por qué.
   */
  const conVersionDeEdicion = !!estado.data?.pending;
  const motivoDeEdicion = "Ya tiene una versión de edición en curso";

  /** SKU Fabricante, SKU XCONS y Nombre: las primeras celdas de Datos principales. */
  const identidad = [
    ...(skuFabricante
      ? [
          <Campo key="skuFabricante" label="SKU Fabricante">
            {skuFabricante}
          </Campo>,
        ]
      : []),
    <Campo key="sku" label={skuFabricante ? "SKU XCONS" : "SKU"}>
      {producto.sku}
    </Campo>,
    <Campo key="name" label="Nombre">
      {producto.values.name ? String(producto.values.name) : <Vacio />}
    </Campo>,
  ];

  return (
    <Flex vertical gap={16}>
      {/* Nombre, botones y Estado del producto fijos al hacer scroll (xcons-docs#479). */}
      <CabeceraFija>
        {/* La cabecera de página del Admin, fuera de la tarjeta: ← de 32×32 a 16px del
            título, que es el nombre del producto. */}
        {/* Nombre a la izquierda y el flujo de estado a la derecha, alineados por arriba
            (xcons-docs#404). Las transiciones están en el menú ▾ del flujo. */}
        <Flex justify="space-between" align="flex-start" gap={16}>
          {/* `minWidth: 0` deja que el nombre se corte con "…": sin eso un nombre largo
              empuja el flujo a la línea de abajo. */}
          <Flex align="center" gap={12} style={{ minWidth: 0 }}>
            <Flex align="center" style={{ minWidth: 0 }}>
              <ListButton
                hideText
                icon={<LeftOutlined />}
                onClick={() => navigate("/catalog")}
                style={{ marginRight: 16, flexShrink: 0 }}
              />
              <Title level={4} style={{ margin: 0 }} ellipsis={{ tooltip: true }}>
                {String(producto.values.name ?? "(sin nombre)")}
              </Title>
            </Flex>
            {/* El tipo de producto, junto al nombre. */}
            {producto.attributeSet.name && (
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                {etiquetaConjunto(producto.attributeSet.name)}
              </Tag>
            )}
          </Flex>
          {/* Duplicar y Editar a la derecha, junto a Permisos (xcons-docs#479). */}
          <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
            <Tooltip
              title={
                !puedeCrear
                  ? "No tenés permisos"
                  : conVersionDeEdicion
                    ? motivoDeEdicion
                    : "Duplicar"
              }
            >
              {/* El `span` deja que el tooltip aparezca sobre el botón deshabilitado. */}
              <span>
                <Button
                  aria-label="Duplicar"
                  icon={<CopyOutlined />}
                  disabled={!puedeCrear || conVersionDeEdicion}
                  onClick={() => setDuplicando(true)}
                />
              </span>
            </Tooltip>
            {/* Con texto y del tamaño de Enviar a revisión (xcons-docs#479). El tooltip queda
                sólo para decir por qué está deshabilitado. */}
            <Tooltip
              title={
                !puedeEditar
                  ? "No tenés permisos"
                  : conVersionDeEdicion
                    ? motivoDeEdicion
                    : undefined
              }
            >
              <span>
                <Button
                  icon={<EditOutlined />}
                  disabled={!puedeEditar || conVersionDeEdicion}
                  onClick={() =>
                    navigate(`/catalog/${encodeURIComponent(producto.sku)}/edit`)
                  }
                >
                  Editar
                </Button>
              </span>
            </Tooltip>
            <PermisosDelProducto
              productId={producto.magentoProductId}
              onChanged={refrescarAcceso}
            />
          </Flex>
        </Flex>

        {/* El flujo en su propia sección, a todo el ancho. Su menú tiene sólo lo que cambia
            el estado: Editar y Duplicar van junto a Permisos. */}
        {estado.data && (
          <ProductStateSteps
            state={estado.data.state}
            acciones={estado.acciones}
            fechas={{ creado: producto.createdAt, editado: producto.updatedAt }}
            anchoCompleto
            // Con una versión de edición encima, un acceso directo a ella junto al estado
            // (xcons-docs#487). Su ficha tiene el de vuelta.
            etiquetas={
              estado.data.pending && (
                <Typography.Link
                  onClick={() =>
                    navigate(`/catalog/pending/${estado.data?.pending?.id}`)
                  }
                >
                  Hay una revisión pendiente de este producto
                </Typography.Link>
              )
            }
          />
        )}
      </CabeceraFija>

      {/* Con acceso al Catálogo pero sin participación el detalle es read-only (F2), y
          **sin banner** (xcons-docs#466): que no se pueda editar ya lo dicen los controles
          deshabilitados. */}
      <ProductStateBar estado={estado} />

      <SeccionesDeLaFicha
        secciones={seccionesDelForm(producto.groups)}
        identidad={identidad}
        valor={(attribute) => valorPublicado(producto, attribute)}
        imagenes={{
          contenido: <ImagenesYVideos media={media} />,
        }}
        extras={[
          {
            key: "adjuntos",
            titulo: "Adjuntos",
            contenido: <Adjuntos attachments={adjuntos} />,
          },
          {
            key: "relacionados",
            titulo: "Productos relacionados",
            contenido: <Relacionados links={producto.links} />,
          },
        ]}
      />

      {/* Categorías va fuera del bloque de la ficha (xcons-docs#479), y con ella la
          visibilidad por tienda: las dos dicen dónde se ve el producto. */}
      <Bloque titulo="Categorías">
        <Flex vertical gap={16}>
          <CategoriasPorArbol
            categories={producto.categories ?? []}
            categoryRoots={producto.categoryRoots ?? []}
            stores={storesConCategorias(identity)}
          />
          <VisibilidadEnTiendas visibilidad={oferta.visibilidad} />
        </Flex>
      </Bloque>

      {/* Va acá, al final de los datos del producto y antes del historial: es contexto
          —quién lo vende— y no un campo de la ficha. */}
      <StoresDelProducto oferta={oferta} />

      {/* Historial del producto (Fase 12), igual al de Presupuestos y Pedidos, con su
          buscador (xcons-docs#404). Dos cosas propias del catálogo:

          - **Se pide por el `entity_id` y no por el SKU de la ruta.** El historial de un
            producto vive partido entre los dos —el borrador se ancla en el SKU, porque
            todavía no existe en Magento—, y es el backend el que junta los dos anclajes.
            Se le pasa el id porque es el que resuelve la correspondencia sin ambigüedad:
            el SKU no es unique en Magento.
          - **Sin notas.** No es una decisión de UI: una nota se graba con un store y sus
            mencionables se resuelven por store/seller, y un producto no tiene ninguno de
            los dos. Ver el prop `notes` del componente. */}
      {producto.magentoProductId && (
        <HistorialDelProducto entityId={producto.magentoProductId} />
      )}

      <DuplicarProducto
        open={duplicando}
        onClose={() => setDuplicando(false)}
        sku={producto.sku}
        marca={skuFabricante && typeof marca === "string" ? marca : null}
      />
    </Flex>
  );
};

/**
 * **Quién vende este producto, y dónde.**
 *
 * El eje es el **vendedor**, no la tienda: la pregunta que contesta es "¿quién lo ofrece?",
 * y una fila por tienda la contestaba repitiendo al mismo vendedor una vez por cada tienda
 * donde está vinculado.
 *
 * **Sólo lo que entra en los permisos de seller de quien mira** (PM, 2026-09-14): sus
 * vendedores, y las tiendas donde esos vendedores están vinculados. Sin excepción para el
 * Admin de catálogo — su bypass es lo que llenaba la pantalla con las 40 tiendas del
 * ecosistema. El `scope` viaja con las filas para poder decir en pantalla qué se está
 * mostrando: una tabla recortada que no avisa que lo está miente por omisión.
 */
const StoresDelProducto: React.FC<{ oferta: OfertaDelProducto }> = ({
  oferta: { filas, scope, loading },
}) => {
  // Los nombres de las tiendas: la oferta trae sólo el código. Si no llegan, va el código.
  const { stores: operatorStores } = useOperatorStores();
  const { token } = theme.useToken();
  const nombreDeStore = useMemo(
    () => new Map(operatorStores.map((s) => [s.code, s.name])),
    [operatorStores],
  );

  const ofrecidas = filas.filter((f) => f.offered);

  /**
   * Un vendedor con las tiendas donde vende **este** producto. Se arma de las mismas filas:
   * el backend ya recortó qué vendedores puede ver esta persona, así que agrupar acá no
   * expone nada que no hubiera viajado.
   */
  const porVendedor = useMemo(() => {
    const mapa = new Map<
      string,
      { code: string; name: string | null; stores: string[] }
    >();
    for (const fila of ofrecidas) {
      for (const seller of fila.sellers) {
        const actual = mapa.get(seller.code) ?? {
          code: seller.code,
          name: seller.name,
          stores: [],
        };
        actual.stores.push(fila.storeCode);
        mapa.set(seller.code, actual);
      }
    }
    return [...mapa.values()].sort((a, b) =>
      (a.name ?? a.code).localeCompare(b.name ?? b.code),
    );
  }, [ofrecidas]);

  // Sin permisos de seller no hay nada que mostrar, y una tarjeta vacía se leería como
  // "este producto no lo vende nadie", que es otra cosa.
  if (!loading && scope?.level === "none") return null;

  // Con el mismo encabezado gris que el resto de las secciones de la ficha (xcons-docs#404),
  // no el de una tarjeta suelta.
  return (
    <>
      <Bloque titulo="Quién lo vende">
        <Flex vertical gap={12}>
          {/* El alcance se declara en pantalla (PM, 2026-09-14 — A2.13): una tabla
              recortada que no avisa que lo está miente por omisión. Una línea corta y no un
              ícono con la lista de sellers (xcons-docs#487). */}
          {scope?.sellerCodes?.length ? (
            <Text type="secondary" italic style={{ fontSize: token.fontSize - 2 }}>
              Sólo ves la oferta de tus vendedores
            </Text>
          ) : null}
          <Table
            rowKey="code"
            size="small"
            tableLayout="fixed"
            style={tablaConBorde(token)}
            loading={loading}
            dataSource={porVendedor}
            pagination={false}
            locale={{
              emptyText: loading
                ? " "
                : "Ninguno de tus vendedores ofrece este producto",
            }}
            columns={[
              {
                title: (
                  <Space size={6}>
                    <SellerIcon />
                    Sellers
                  </Space>
                ),
                width: "50%",
                dataIndex: "name",
                key: "name",
                render: (name: string | null, row) => (
                  <Text strong>{name ?? row.code}</Text>
                ),
              },
              {
                title: "Lo vende en",
                width: "50%",
                dataIndex: "stores",
                key: "stores",
                render: (stores: string[]) => (
                  <Space wrap size={12}>
                    {stores.map((s) => (
                      <Space key={s} size={6}>
                        <ShopOutlined />
                        <Text>{nombreDeStore.get(s) ?? s}</Text>
                      </Space>
                    ))}
                  </Space>
                ),
              },
            ]}
          />
        </Flex>
      </Bloque>
    </>
  );
};

/** Las tablas de la oferta con el mismo borde y las columnas mitad y mitad, para que se lean juntas. */
const tablaConBorde = (
  token: ReturnType<typeof theme.useToken>["token"],
): React.CSSProperties => ({
  border: `1px solid ${token.colorBorderSecondary}`,
  borderRadius: token.borderRadiusLG,
  overflow: "hidden",
});

interface OfertaDelProducto {
  filas: StoreOfferRow[];
  scope: OfferScope | null;
  visibilidad: StoreVisibilityRow[];
  loading: boolean;
}

/** `GET /pim/products/:id/stores`: quién lo vende, y su visibilidad en cada tienda. */
const useOfertaDelProducto = (
  productId: number | null | undefined,
): OfertaDelProducto => {
  const [filas, setFilas] = useState<StoreOfferRow[]>([]);
  const [scope, setScope] = useState<OfferScope | null>(null);
  const [visibilidad, setVisibilidad] = useState<StoreVisibilityRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    let cancelado = false;
    setLoading(true);
    axiosInstance
      .get<ProductStoresResponse>(`${API_URL_PIM_PRODUCTS}/${productId}/stores`)
      .then(({ data }) => {
        if (cancelado) return;
        setFilas(data.rows ?? []);
        setScope(data.scope ?? null);
        setVisibilidad(data.visibility ?? []);
      })
      .catch(() => !cancelado && setFilas([]))
      .finally(() => !cancelado && setLoading(false));
    return () => {
      cancelado = true;
    };
  }, [productId]);

  return { filas, scope, visibilidad, loading };
};

/**
 * **Visibilidad por tienda** (Excel: "Visibilidad en XCONS / [STORE]"). Es la salida del
 * predicado de oferta, no la carga nadie. Va dentro de Categorías, con su propio título: su
 * recorte es otro que el de "Quién lo vende" —las tiendas donde quien mira tiene **permiso de
 * store**—. Un seller no ve la de ninguna, porque la visibilidad de una tienda depende de
 * todos sus vendedores y le contaría si la competencia lo vende ahí. Sin filas, no se muestra.
 */
const VisibilidadEnTiendas: React.FC<{ visibilidad: StoreVisibilityRow[] }> = ({
  visibilidad,
}) => {
  const { token } = theme.useToken();
  if (!visibilidad.length) return null;

  return (
    <Flex vertical gap={8}>
      <Text strong>Visibilidad en tus tiendas</Text>
      <Table
        rowKey="storeCode"
        size="small"
        tableLayout="fixed"
        style={tablaConBorde(token)}
        pagination={false}
        dataSource={visibilidad}
        columns={[
          {
            title: "Tienda",
            width: "50%",
            dataIndex: "storeCode",
            key: "storeCode",
            render: (code: string) => <Tag>{code}</Tag>,
          },
          {
            title: "Visibilidad",
            width: "50%",
            dataIndex: "visible",
            key: "visible",
            render: (visible: boolean) =>
              visible ? <Tag color="green">Visible</Tag> : <Tag>Oculto</Tag>,
          },
        ]}
      />
    </Flex>
  );
};
